import { useState, useRef, useEffect } from "react";
import { Wifi, Waypoints, Sparkles, LogOut, QrCode, Shield, Settings, Route, Download, LifeBuoy, BookMarked, Router, CalendarClock, Layers, ChevronDown, ChevronLeft, ChevronRight, Users, Split, Activity, Radar, Milestone, Server, FolderInput, LayoutGrid, ShieldCheck, FileSignature, BarChart3, Receipt, Warehouse, Briefcase, Network, Gauge, Star, Globe, Wand2, KeyRound } from "lucide-react";
import { MODULE_ORDER, MODULES } from "@/lib/modules";
import { buildMenuModel, GROUP_META } from "@/lib/menuLayout";
import { WIFI_TABS } from "@/lib/wifiTabs";
import { useI18n } from "@/context/I18nContext";
import { useBranding } from "@/context/BrandingContext";

const HIDDEN_KEYS = ["cable_catalog", "closure_catalog", "odf_catalog", "documents", "technicians"];
const GROUPS = [
  { id: "infra", label: "Infrastruktura", icon: Milestone, keys: ["poles", "routes", "ducts", "wells", "microducts", "pipelines", "coverage", "zones"], actions: [] },
  { id: "devices", label: "Urządzenia", icon: Server, keys: ["olt", "olt_cards", "switches", "onu", "odf", "patchpanels"], actions: ["snmp", "ping"] },
  { id: "subscribers", label: "Abonenci", icon: Users, keys: ["clients", "client_cables"], actions: [] },
  { id: "fiber", label: "Sieć światłowodowa", icon: Split, keys: ["cables", "cable_slack", "closures", "splitters", "splices"], actions: ["connections", "reservations", "qrlabels", "otdrfiles"] },
  { id: "io", label: "Import / Eksport", icon: FolderInput, keys: [], actions: ["export"] },
];
const GROUPED_KEYS = GROUPS.flatMap((g) => g.keys);
const WIFI_ICONS = { dashboard: Gauge, sites: Milestone, sectors: Radar, links: Waypoints, devices: Server, models: Layers, audit: CalendarClock, subscribers: Users, ip: Network, planning: Wand2, monitoring: Activity };

function WifiGroup({ t, open, onToggle, onPick }) {
  return (
    <div className="w-full">
      <button onClick={onToggle} data-testid="sidebar-group-wifi"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-slate-300 hover:bg-slate-800 hover:text-cyan-300">
        <Wifi size={18} className="shrink-0" /><span className="truncate flex-1 text-left">{t("Sieć WiFi")}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-0.5 mb-1 ml-3.5 pl-2 border-l border-slate-800 flex flex-col gap-0.5">
          {WIFI_TABS.map(([k, l]) => {
            const CIcon = WIFI_ICONS[k] || Wifi;
            return (
              <button key={k} onClick={() => onPick(k)} data-testid={`sidebar-wifi-${k}`}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors text-slate-400 hover:bg-slate-800 hover:text-cyan-300">
                <CIcon size={16} className="shrink-0" /><span className="truncate">{t(l)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NavItem({ icon: Icon, label, onClick, active, testid, accent = "cyan" }) {
  const accentCls = {
    cyan: "hover:text-cyan-400", pink: "hover:text-pink-400", green: "hover:text-green-400", red: "hover:text-red-400",
  }[accent] || "hover:text-cyan-400";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex flex-col items-center gap-1.5 py-2.5 rounded-lg transition-colors ${active ? "bg-blue-600 text-white" : `text-slate-400 hover:bg-slate-800 ${accentCls}`}`}>
      <Icon size={26} />
      <span className="text-[12px] leading-tight text-center px-1 w-full whitespace-normal break-words">{label}</span>
    </button>
  );
}

// Horizontal item (top bar / macOS dock)
function HItem({ icon: Icon, label, onClick, active, testid, dock }) {
  if (dock) {
    return (
      <button onClick={onClick} data-testid={testid} title={label}
        className="group relative flex flex-col items-center gap-1 shrink-0 w-[66px] pt-1 pb-1 origin-bottom transition-transform hover:-translate-y-1">
        <Icon size={27} strokeWidth={1.8}
          className={`transition-colors ${active ? "text-cyan-400 drop-shadow-[0_0_6px_rgba(34,211,238,0.55)]" : "text-slate-300 group-hover:text-cyan-300"}`} />
        <span className={`text-[10px] leading-tight text-center w-full truncate transition-colors ${active ? "text-cyan-300" : "text-slate-400 group-hover:text-slate-200"}`}>{label}</span>
        {active && <span className="absolute bottom-0 w-1.5 h-1.5 rounded-full bg-cyan-400" />}
      </button>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid} title={label}
      className="group relative flex flex-col items-center justify-end shrink-0">
      <span className={`flex items-center justify-center rounded-xl transition-colors w-10 h-10 ${active ? "bg-blue-600 text-white shadow-lg" : "bg-slate-800/70 text-slate-300 group-hover:bg-slate-700 group-hover:text-cyan-300"}`}>
        <Icon size={20} />
      </span>
      <span className="mt-0.5 text-[10px] leading-none max-w-[70px] truncate text-slate-400">{label}</span>
    </button>
  );
}

export default function Sidebar({ active, onSelect, onAI, onLogout, onQRLabels, onOtdr, onUsers, onSecurity, onRequests, onInvoices, onWarehouse, onRouters, onSwitches, onWifi, onDashboard, onClient360, onStats, onPitUke, onSidusis, onAutoPlan, onSettings, onLicenses, onConnections, onExport, onHelp, onReservations, onSNMP, onPing, onSchedule, user, menuLayout, variant = "left", pendingRenewals = 0, isMaster = false }) {
  const { t } = useI18n();
  const { branding } = useBranding();
  const restricted = user?.department === "ksiegowosc";
  const mainKeys = (restricted ? ["clients"] : MODULE_ORDER).filter((k) => !HIDDEN_KEYS.includes(k));
  const leftoverKeys = mainKeys.filter((k) => !GROUPED_KEYS.includes(k));
  const [openGroups, setOpenGroups] = useState({ infra: false, devices: false, subscribers: false, fiber: false, io: false, management: false, system: false });
  const toggleGroup = (id) => setOpenGroups((s) => (s[id] ? {} : { [id]: true }));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDockGroup, setOpenDockGroup] = useState(null);
  const [fanReady, setFanReady] = useState(false);
  const dockScrollRef = useRef(null);
  useEffect(() => {
    if (!openDockGroup) return undefined;
    setFanReady(false);
    const id = requestAnimationFrame(() => setFanReady(true));
    return () => cancelAnimationFrame(id);
  }, [openDockGroup]);
  const fanPos = (i, n) => {
    if (n > 7) {
      const half = Math.ceil(n / 2);
      const ring = i < half ? 0 : 1;
      const m = ring ? n - half : half;
      const j = ring ? i - half : i;
      const ang = m <= 1 ? 90 : (160 - j * (140 / (m - 1)));
      const r = ring ? 240 : 150;
      const rad = (ang * Math.PI) / 180;
      return { x: Math.cos(rad) * r, y: Math.sin(rad) * r };
    }
    const ang = n <= 1 ? 90 : (150 - i * (120 / (n - 1)));
    const r = 78 + n * 7;
    const rad = (ang * Math.PI) / 180;
    return { x: Math.cos(rad) * r, y: Math.sin(rad) * r };
  };

  const ACTIONS = {
    snmp: { label: "Monitoring", icon: Activity, onClick: onSNMP, accent: "green", testid: "sidebar-snmp" },
    ping: { label: "Ping", icon: Radar, onClick: onPing, accent: "green", testid: "sidebar-ping" },
    connections: { label: "Połączenia", icon: Route, onClick: onConnections, accent: "cyan", testid: "sidebar-connections" },
    reservations: { label: "Rezerwacje", icon: BookMarked, onClick: onReservations, accent: "cyan", testid: "sidebar-reservations" },
    qrlabels: { label: "Etykiety QR", icon: QrCode, onClick: onQRLabels, accent: "cyan", testid: "sidebar-qrlabels" },
    otdrfiles: { label: "Pomiary OTDR", icon: BarChart3, onClick: onOtdr, accent: "cyan", testid: "sidebar-otdrfiles" },
    export: { label: "Eksport", icon: Download, onClick: onExport, accent: "cyan", testid: "sidebar-export" },
  };

  const Brand = (
    <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 overflow-hidden"
      style={branding.brand_accent ? { backgroundColor: branding.brand_accent } : undefined} title={branding.brand_name || "Piotr Niemiec Fiber 1"}>
      {branding.logo_data_url
        ? <img src={branding.logo_data_url} alt="logo" className="w-full h-full object-contain" data-testid="sidebar-brand-logo" />
        : <Waypoints className="text-white" size={28} />}
    </div>
  );

  // ---- Top bar (poziomy pasek u góry, płaska lista) ----
  if (variant === "top") {
    const items = [];
    mainKeys.forEach((key) => items.push({ icon: MODULES[key].icon, label: t(MODULES[key].label), onClick: () => onSelect(key), active: active === key, testid: `sidebar-${key}` }));
    if (!restricted) ["snmp", "ping", "connections", "reservations", "qrlabels", "otdrfiles", "export"].forEach((aid) => { const a = ACTIONS[aid]; items.push({ icon: a.icon, label: t(a.label), onClick: a.onClick, testid: a.testid }); });
    if (!restricted) {
      items.push({ icon: CalendarClock, label: t("Terminarz"), onClick: onSchedule, testid: "sidebar-schedule" });
      items.push({ icon: Sparkles, label: t("Asystent AI"), onClick: onAI, testid: "sidebar-ai" });
    }
    items.push({ icon: Settings, label: t("Ustawienia"), onClick: onSettings, testid: "sidebar-settings" });
    if (user?.role === "admin" && isMaster) items.push({ icon: KeyRound, label: t("Licencje"), onClick: onLicenses, testid: "sidebar-licenses" });
    if (user?.role === "admin") items.push({ icon: Shield, label: t("Pracownicy"), onClick: onUsers, testid: "sidebar-users" });
    if (user?.role === "admin") items.push({ icon: ShieldCheck, label: t("Bezpieczeństwo"), onClick: onSecurity, testid: "sidebar-security" });
    if (user?.role === "admin") items.push({ icon: FileSignature, label: t("Wnioski / Aneksy"), onClick: onRequests, testid: "sidebar-requests" });
    if (user?.role === "admin") items.push({ icon: Receipt, label: t("Faktury"), onClick: onInvoices, testid: "sidebar-invoices" });
    items.push({ icon: Warehouse, label: t("Magazyn"), onClick: onWarehouse, testid: "sidebar-warehouse" });
    if (!restricted) items.push({ icon: Gauge, label: t("Pulpit NOC"), onClick: onDashboard, testid: "sidebar-dashboard" });
    items.push({ icon: Users, label: t("Widok 360°"), onClick: onClient360, testid: "sidebar-client360" });
    if (!restricted) items.push({ icon: Router, label: t("Routery"), onClick: onRouters, testid: "sidebar-routers" });
    if (!restricted) items.push({ icon: Network, label: t("Switche"), onClick: onSwitches, testid: "sidebar-switches" });
    if (!restricted && onWifi) items.push({ icon: Wifi, label: t("Sieć WiFi"), onClick: () => onWifi(), testid: "sidebar-wifi" });
    items.push({ icon: Milestone, label: t("PIT / UKE"), onClick: onPitUke, testid: "sidebar-pituke" });
    items.push({ icon: Globe, label: t("SIDUSIS"), onClick: onSidusis, testid: "sidebar-sidusis" });
    items.push({ icon: Wand2, label: t("Auto-projekt"), onClick: onAutoPlan, testid: "sidebar-autoplan" });
    if (!restricted) items.push({ icon: BarChart3, label: t("Statystyki"), onClick: onStats, testid: "sidebar-stats" });
    if (!restricted) items.push({ icon: LifeBuoy, label: t("Pomoc"), onClick: onHelp, testid: "sidebar-help" });
    items.push({ icon: LogOut, label: t("Wyloguj"), onClick: onLogout, testid: "logout-button" });
    return (
      <div className="absolute top-0 left-0 right-0 z-[800] h-[70px] flex items-center gap-1 px-3 bg-slate-950/95 border-b border-slate-800 overflow-x-auto no-scrollbar" data-testid="sidebar-top">
        <div className="shrink-0 mr-1">{Brand}</div>
        {items.map((it, i) => <HItem key={it.testid + i} {...it} dock={false} />)}
      </div>
    );
  }

  // ---- Dock (macOS): kompaktowy, przewijany, kategorie rozwijają się łukiem w górę ----
  if (variant === "dock") {
    const catItems = GROUPS.map((g) => {
      const kids = [
        ...g.keys.filter((k) => mainKeys.includes(k)).map((k) => ({ icon: MODULES[k].icon, label: t(MODULES[k].label), onClick: () => onSelect(k), testid: `dock-${k}` })),
        ...((restricted ? [] : (g.actions || [])).map((aid) => ({ icon: ACTIONS[aid].icon, label: t(ACTIONS[aid].label), onClick: ACTIONS[aid].onClick, testid: ACTIONS[aid].testid }))),
      ];
      return { id: g.id, icon: g.icon, label: t(g.label), children: kids };
    }).filter((g) => g.children.length);
    const loneItems = leftoverKeys.map((k) => ({ id: k, icon: MODULES[k].icon, label: t(MODULES[k].label), onClick: () => onSelect(k), testid: `dock-${k}` }));
    const mgmtKids = [
      { icon: Gauge, label: t("Pulpit NOC"), onClick: onDashboard, testid: "dock-dashboard" },
      { icon: Users, label: t("Widok 360°"), onClick: onClient360, testid: "dock-client360" },
      { icon: Warehouse, label: t("Magazyn"), onClick: onWarehouse, testid: "dock-warehouse" },
      ...(!restricted ? [{ icon: Router, label: t("Routery"), onClick: onRouters, testid: "dock-routers" }] : []),
      ...(!restricted ? [{ icon: Network, label: t("Switche"), onClick: onSwitches, testid: "dock-switches" }] : []),
      ...(!restricted && onWifi ? [{ icon: Wifi, label: t("Sieć WiFi"), onClick: () => onWifi(), testid: "dock-wifi" }] : []),
      ...(!restricted ? [{ icon: BarChart3, label: t("Statystyki"), onClick: onStats, testid: "dock-stats" }] : []),
      ...(user?.role === "admin" ? [{ icon: Shield, label: t("Pracownicy"), onClick: onUsers, testid: "dock-users" }, { icon: FileSignature, label: t("Wnioski / Aneksy"), onClick: onRequests, testid: "dock-requests" }, { icon: Receipt, label: t("Faktury"), onClick: onInvoices, testid: "dock-invoices" }] : []),
    ];
    const sysKids = [
      ...(!restricted ? [{ icon: CalendarClock, label: t("Terminarz"), onClick: onSchedule, testid: "dock-schedule" }, { icon: Sparkles, label: t("Asystent AI"), onClick: onAI, testid: "dock-ai" }] : []),
      { icon: Settings, label: t("Ustawienia"), onClick: onSettings, testid: "dock-settings" },
      ...(user?.role === "admin" && isMaster ? [{ icon: KeyRound, label: t("Licencje"), onClick: onLicenses, testid: "dock-licenses" }] : []),
      ...(user?.role === "admin" ? [{ icon: ShieldCheck, label: t("Bezpieczeństwo"), onClick: onSecurity, testid: "dock-security" }] : []),
      { icon: Milestone, label: t("PIT / UKE"), onClick: onPitUke, testid: "dock-pituke" },
      { icon: Globe, label: t("SIDUSIS"), onClick: onSidusis, testid: "dock-sidusis" },
      { icon: Wand2, label: t("Auto-projekt"), onClick: onAutoPlan, testid: "dock-autoplan" },
      ...(!restricted ? [{ icon: LifeBuoy, label: t("Pomoc"), onClick: onHelp, testid: "dock-help" }] : []),
      { icon: LogOut, label: t("Wyloguj"), onClick: onLogout, testid: "logout-button" },
    ];
    const wifiCat = !restricted && onWifi ? [{ id: "wifi", icon: Wifi, label: t("Sieć WiFi"), children: WIFI_TABS.map(([k, l]) => ({ icon: WIFI_ICONS[k] || Wifi, label: t(l), onClick: () => onWifi(k), testid: `dock-wifi-${k}` })) }] : [];
    const dockItems = [...catItems, ...wifiCat, ...loneItems,
      ...(mgmtKids.length ? [{ id: "management", icon: Briefcase, label: t("Zarządzanie"), children: mgmtKids }] : []),
      { id: "system", icon: LayoutGrid, label: t("System"), children: sysKids }];
    const scrollBy = (dx) => dockScrollRef.current && dockScrollRef.current.scrollBy({ left: dx, behavior: "smooth" });
    const openGroup = dockItems.find((it) => it.children && it.id === openDockGroup);
    return (
      <>
        {openDockGroup && <div className="fixed inset-0 z-[790]" data-testid="dock-backdrop" onClick={() => setOpenDockGroup(null)} />}
        {/* Fan (łuk) renderowany osobno nad dokiem — poza kontenerem scrolla (brak przycinania) */}
        {openGroup && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-[801]" style={{ width: 0, height: 0 }} data-testid="dock-fan">
            {openGroup.children.map((c, i) => {
              const { x, y } = fanPos(i, openGroup.children.length);
              const tf = fanReady ? `translate(calc(-50% + ${x}px), ${-y}px)` : "translate(-50%, 10px)";
              const CIcon = c.icon;
              return (
                <button key={c.testid} data-testid={c.testid} title={c.label}
                  onClick={(e) => { e.stopPropagation(); if (c.onClick) c.onClick(); setOpenDockGroup(null); }}
                  className="group/fan absolute bottom-0 flex flex-col items-center justify-center gap-0.5 w-16 h-16 rounded-2xl bg-slate-900/90 backdrop-blur-xl text-slate-200 border border-cyan-500/25 shadow-2xl shadow-cyan-950/30 hover:bg-cyan-500/20 hover:text-cyan-200 hover:border-cyan-400/50 transition-colors"
                  style={{ transform: tf, opacity: fanReady ? 1 : 0, transition: "transform .32s cubic-bezier(.2,1.3,.3,1), opacity .2s", transitionDelay: `${i * 35}ms` }}>
                  <CIcon size={22} strokeWidth={1.8} />
                  <span className="text-[9px] leading-tight text-center px-0.5 w-full truncate">{c.label}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[800] flex items-end gap-1.5" data-testid="sidebar-dock">
          <button onClick={() => scrollBy(-220)} data-testid="dock-scroll-left" className="w-8 h-8 shrink-0 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center"><ChevronLeft size={18} /></button>
          <div ref={dockScrollRef} className="flex items-end gap-1 px-3 py-2 rounded-2xl kv-glass shadow-2xl shadow-cyan-950/30 overflow-x-auto no-scrollbar" style={{ maxWidth: "min(94vw, 640px)" }}>
            {dockItems.map((it) => {
              const isCat = !!it.children;
              const activeItem = isCat ? openDockGroup === it.id : active === it.id;
              return (
                <HItem key={it.id} icon={it.icon} label={it.label} active={activeItem} testid={`dock-cat-${it.id}`} dock
                  onClick={() => (isCat ? setOpenDockGroup(openDockGroup === it.id ? null : it.id) : it.onClick())} />
              );
            })}
          </div>
          <button onClick={() => scrollBy(220)} data-testid="dock-scroll-right" className="w-8 h-8 shrink-0 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center"><ChevronRight size={18} /></button>
        </div>
      </>
    );
  }

  // ---- Vertical variants (left / right) — kompaktowe zwijane grupy + personalizacja ----
  const isRight = variant === "right";
  const outer = `absolute top-0 bottom-0 w-52 z-[810] bg-slate-950 flex flex-col py-3 px-2 transition-transform duration-200 md:translate-x-0 ${isRight ? `right-0 border-l border-slate-800 ${mobileOpen ? "translate-x-0" : "translate-x-full"}` : `left-0 border-r border-slate-800 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}`;
  const admin = user?.role === "admin";

  // Rejestr uchwytów (ikona + akcja) dla każdego id pozycji menu.
  const H = {};
  mainKeys.forEach((k) => { if (MODULES[k]) H[k] = { icon: MODULES[k].icon, onClick: () => onSelect(k), navKey: k }; });
  if (!restricted) {
    H.snmp = { icon: Activity, onClick: onSNMP }; H.ping = { icon: Radar, onClick: onPing };
    H.connections = { icon: Route, onClick: onConnections }; H.reservations = { icon: BookMarked, onClick: onReservations };
    H.qrlabels = { icon: QrCode, onClick: onQRLabels }; H.export = { icon: Download, onClick: onExport };
    H.otdrfiles = { icon: BarChart3, onClick: onOtdr };
    H.dashboard = { icon: Gauge, onClick: onDashboard }; H.routers = { icon: Router, onClick: onRouters };
    H.switches = { icon: Network, onClick: onSwitches }; H.wifi = { icon: Wifi, onClick: () => onWifi() }; H.stats = { icon: BarChart3, onClick: onStats };
    H.schedule = { icon: CalendarClock, onClick: onSchedule }; H.ai = { icon: Sparkles, onClick: onAI };
    H.help = { icon: LifeBuoy, onClick: onHelp };
  }
  H.warehouse = { icon: Warehouse, onClick: onWarehouse };
  H.client360 = { icon: Users, onClick: onClient360 };
  H.settings = { icon: Settings, onClick: onSettings };
  H.pituke = { icon: Milestone, onClick: onPitUke };
  H.sidusis = { icon: Globe, onClick: onSidusis };
  H.autoplan = { icon: Wand2, onClick: onAutoPlan };
  if (admin) {
    H.users = { icon: Shield, onClick: onUsers }; H.requests = { icon: FileSignature, onClick: onRequests };
    H.invoices = { icon: Receipt, onClick: onInvoices }; H.security = { icon: ShieldCheck, onClick: onSecurity };
  }
  if (admin && isMaster) H.licenses = { icon: KeyRound, onClick: onLicenses };

  const layout = menuLayout || {};
  const hiddenSet = new Set(layout.hidden || []);
  const move = layout.move || {};
  const order = layout.order || {};
  const customGroups = (layout.custom_groups && layout.custom_groups.length)
    ? layout.custom_groups
    : (layout.custom_label ? [{ id: "custom", label: layout.custom_label }] : []);
  const customIds = new Set(customGroups.map((g) => g.id));
  const fixedIds = new Set(["infra", "devices", "subscribers", "fiber", "io", "management", "system"]);
  const model = buildMenuModel(t, { restricted, admin, master: isMaster });
  const groupOf = (m) => { const g = move[m.id]; return (g && (fixedIds.has(g) || customIds.has(g) || g === "_top")) ? g : m.dg; };
  const items = model.filter((m) => H[m.id] && !hiddenSet.has(m.id)).map((m) => ({ ...m, ...H[m.id], group: groupOf(m) }));
  const GICONS = { infra: Milestone, devices: Server, subscribers: Users, fiber: Split, io: FolderInput, management: Briefcase, system: LayoutGrid };
  const sortKids = (arr, gid) => { const o = order[gid] || []; return [...arr].sort((a, b) => { const ia = o.indexOf(a.id), ib = o.indexOf(b.id); return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib); }); };
  const fixedMeta = GROUP_META.filter((g) => g.id !== "_top" && g.id !== "custom").map((g) => ({ id: g.id, icon: GICONS[g.id] || LayoutGrid, label: t(g.label) }));
  const groupsMeta = [...fixedMeta, ...customGroups.map((g) => ({ id: g.id, icon: Star, label: g.label || t("Moje skróty") }))];
  const topItems = sortKids(items.filter((i) => i.group === "_top"), "_top");
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <button onClick={() => setMobileOpen((v) => !v)} data-testid="sidebar-mobile-toggle"
        className={`md:hidden fixed top-2 ${isRight ? "right-2" : "left-2"} z-[905] bg-slate-900/90 border border-slate-700 rounded-lg p-2 text-slate-200 shadow-lg`}>
        {mobileOpen ? <ChevronLeft size={20} /> : <Layers size={20} />}
      </button>
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-[805]" onClick={closeMobile} data-testid="sidebar-backdrop" />}
    <div className={outer} data-testid={`sidebar-${variant}`}>
      <div className="flex items-center gap-2 px-1 mb-3 shrink-0">{Brand}
        <span className="text-sm font-semibold text-slate-200 truncate">{branding.brand_name || "FiberPass"}</span>
      </div>
      <div className="flex-1 overflow-y-auto w-full flex flex-col gap-0.5 no-scrollbar">
        {topItems.map((it) => {
          if (it.id === "wifi") return <WifiGroup key="wifi" t={t} open={!!openGroups.wifi} onToggle={() => toggleGroup("wifi")} onPick={(k) => { onWifi(k); closeMobile(); }} />;
          const IIcon = it.icon;
          return (
            <button key={it.id} onClick={() => { it.onClick(); closeMobile(); }} data-testid={`sidebar-${it.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${it.navKey && active === it.navKey ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-cyan-300"}`}>
              <IIcon size={18} className="shrink-0" /><span className="truncate">{it.label}</span>
            </button>
          );
        })}
        {groupsMeta.map((g) => {
          const kids = sortKids(items.filter((i) => i.group === g.id), g.id);
          if (!kids.length) return null;
          const open = openGroups[g.id];
          const GIcon = g.icon;
          const hasActive = kids.some((c) => c.navKey && active === c.navKey);
          return (
            <div key={g.id} className="w-full">
              <button onClick={() => toggleGroup(g.id)} data-testid={`sidebar-group-${g.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-slate-800 hover:text-cyan-300 ${hasActive && !open ? "text-cyan-300" : "text-slate-300"}`}>
                <GIcon size={18} className="shrink-0" />
                <span className="truncate flex-1 text-left">{g.label}</span>
                {g.id === "system" && pendingRenewals > 0 && (
                  <span data-testid="system-pending-badge" className="min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{pendingRenewals > 99 ? "99+" : pendingRenewals}</span>
                )}
                <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="mt-0.5 mb-1 ml-3.5 pl-2 border-l border-slate-800 flex flex-col gap-0.5">
                  {kids.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <button key={c.id} onClick={() => { c.onClick(); closeMobile(); }} data-testid={`sidebar-${c.id}`}
                        className={`relative w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${c.navKey && active === c.navKey ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-cyan-300"}`}>
                        <CIcon size={16} className="shrink-0" /><span className="truncate">{c.label}</span>
                        {c.id === "settings" && pendingRenewals > 0 && (
                          <span data-testid="settings-pending-badge" className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{pendingRenewals > 99 ? "99+" : pendingRenewals}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={onLogout} title={`${t("Wyloguj")} (${user?.name})`} data-testid="logout-button"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-red-900/40 hover:text-red-400 shrink-0 mt-1.5">
        <LogOut size={18} className="shrink-0" /><span className="truncate">{t("Wyloguj")}</span>
      </button>
    </div>
    </>
  );
}
